// Created on 2023-08-03
// Created by Vitaut Tryputsin
//
// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { InteractiveObject } from "../object/interactive-object.js";
import { ObjectListSeparator } from "../types/base-types.js";
/**
 * Interactive clipping plane object which can be added to the scene.
 * This class exposes properties of C++ class `Graphic3d_ClipPlane` defined by OCCT.
 */
export class InteractiveClippingPlane extends InteractiveObject {
    constructor(theModule, theId) {
        super(theModule, theId, "_clip_plane_");
        this._myClip = theModule.clippingPlane;
        this._myClip.id = this.id;
        ;
    }
    /**
     * Get or set the objects that the clipping plane applies to.
     */
    get objects() {
        this._myClip.id = this.id;
        return this._myClip.objects;
    }
    set objects(theObjects) {
        this._myClip.id = this.id;
        this._myClip.objects = theObjects;
    }
    /**
     * Get or set the origin of the clipping plane.
     */
    get origin() {
        this._myClip.id = this.id;
        return this._myClip.origin;
    }
    set origin(theOrigin) {
        this._myClip.id = this.id;
        this._myClip.origin = theOrigin;
    }
    /**
     * Get or set the normal of the clipping plane.
     */
    get normal() {
        this._myClip.id = this.id;
        return this._myClip.normal;
    }
    set normal(theNormal) {
        this._myClip.id = this.id;
        this._myClip.normal = theNormal;
    }
    /**
     * Get or set the offset of the clipping plane on the plane axis.
     */
    get offset() {
        this._myClip.id = this.id;
        return this._myClip.offset;
    }
    set offset(theOffset) {
        this._myClip.id = this.id;
        this._myClip.offset = theOffset;
    }
    /**
     * Get or set the flag that signals whether the origin depends on AABB size of selected objects.
     */
    get isRelative() {
        this._myClip.id = this.id;
        return this._myClip.isRelative;
    }
    set isRelative(theIsRelative) {
        this._myClip.id = this.id;
        this._myClip.isRelative = theIsRelative;
    }
    /**
     * Get or set the capping enabled/disabled state of the clipping plane.
     */
    get isCapping() {
        this._myClip.id = this.id;
        return this._myClip.isCapping;
    }
    set isCapping(theIsCapping) {
        this._myClip.id = this.id;
        this._myClip.isCapping = theIsCapping;
    }
    /**
     * Get or set the clipping plane enabled state.
     */
    get visible() {
        this._myClip.id = this.id;
        return this._myClip.isEnabled;
    }
    set visible(theIsEnabled) {
        this._myClip.id = this.id;
        this._myClip.isEnabled = theIsEnabled;
    }
    /**
     * Get or set the clipping plane interactivity state.
     */
    get isInteractive() {
        this._myClip.id = this.id;
        return this._myClip.isInteractive;
    }
    set isInteractive(theIsInteractive) {
        this._myClip.id = this.id;
        this._myClip.isInteractive = theIsInteractive;
    }
    /**
     * Get or set the visualization only enabled/disabled state of the clipping plane.
     */
    get isVisualizationOnly() {
        this._myClip.id = this.id;
        return this._myClip.isVisualizationOnly;
    }
    set isVisualizationOnly(theIsVisualizationOnly) {
        this._myClip.id = this.id;
        this._myClip.isVisualizationOnly = theIsVisualizationOnly;
    }
    /**
     * Get the minimum clipping plane position coordinate on the plane axis.
     */
    get minAxisPosition() {
        this._myClip.id = this.id;
        return this._myClip.minAxisPosition;
    }
    /**
     * Get the maximum clipping plane position coordinate on the plane axis.
     */
    get maxAxisPosition() {
        this._myClip.id = this.id;
        return this._myClip.maxAxisPosition;
    }
    /**
     * Get the current slice number.
     */
    get sliceNb() {
        this._myClip.id = this.id;
        return this._myClip.sliceNb;
    }
    /**
     * Set the current slice number.
     */
    set sliceNb(theSliceNumber) {
        this._myClip.id = this.id;
        this._myClip.sliceNb = theSliceNumber;
    }
    /**
     * Get the number of slices.
     */
    get sliceCount() {
        this._myClip.id = this.id;
        return this._myClip.sliceCount;
    }
    /**
     * Get the slice portion size.
     */
    get sliceSize() {
        this._myClip.id = this.id;
        return this._myClip.sliceSize;
    }
    /**
     * Set the slice portion size.
     */
    set sliceSize(theSliceSize) {
        this._myClip.id = this.id;
        this._myClip.sliceSize = theSliceSize;
    }
    /**
     * Get the auto update bounding box state.
     */
    get isAutoUpdateBbox() {
        this._myClip.id = this.id;
        return this._myClip.isAutoUpdateBbox;
    }
    /**
     * Set the auto update bounding box state.
     */
    set isAutoUpdateBbox(theIsAutoUpdateBbox) {
        this._myClip.id = this.id;
        this._myClip.isAutoUpdateBbox = theIsAutoUpdateBbox;
    }
    /**Update bounding box */
    updateBoundingBox() {
        this._myClip.id = this.id;
        this._myClip.updateBoundingBox();
    }
    /**
     * Get the list of clipping plane sequence ids.
     */
    get clipPlaneSequence() {
        this._myClip.id = this.id;
        return this._myClip.clipPlaneSequence;
    }
    /**
     * Set the clipping plane sequence ids with logical and (conjunction) applied to the sequence of clipping planes.
     */
    set clipPlaneSequence(thePlaneSequenceIds) {
        this._myClip.id = this.id;
        this._myClip.setClipPlaneSequence(thePlaneSequenceIds, '');
    }
    /**
     * Set the clipping plane sequence ids with logical and (conjunction) applied to the sequence of clipping planes.
     */
    setPlaneSequence(...thePlaneSequence) {
        this._myClip.id = this.id;
        this._myClip.setClipPlaneSequence(thePlaneSequence.map((thePlane) => thePlane.id).join(ObjectListSeparator), '');
    }
    /**
     * Set  of clipping planes.
     * @param thePlaneSequence List of clipping plane sequence ids.
     * @param theApplyAnd Apply logical and (conjunction) if true, logical or (disjunction) otherwise.
      */
    setPlaneSequenceIds(theAndPlaneSequence, theOrPlaneSequence) {
        this._myClip.id = this.id;
        this._myClip.setClipPlaneSequence(theAndPlaneSequence, theOrPlaneSequence);
    }
    /**
     * Clone
    */
    clone() {
        return new InteractiveClippingPlane(this._myViewer, super._clone());
    }
}
